/**
 * dirtyGuard — lets a form announce "I have unsaved edits" so navigation
 * chrome (the Settings shell's Esc/Back/rail, for now) can confirm before
 * discarding them (settings redesign slice 4, review item: AI
 * Personalization silently dropped a filled form on Esc).
 *
 * A form registers while dirty and MUST unregister on save/reset/unmount:
 *
 *   useEffect(() => {
 *     if (!isDirty) return undefined;
 *     return registerDirty('AI personalization');
 *   }, [isDirty]);
 *
 * Chrome asks before navigating:
 *
 *   if (!(await confirmDiscard())) return;  // user kept editing
 */

import { confirmDialog } from '../components/ConfirmDialog';

const entries = new Map();
let nextId = 1;

/** Mark a form dirty. Returns an unregister function. */
export function registerDirty(label) {
  const id = nextId++;
  entries.set(id, label || 'This form');
  return () => entries.delete(id);
}

export function hasDirty() {
  return entries.size > 0;
}

/**
 * True → safe to navigate (nothing dirty, or the user chose to discard).
 * False → stay put.
 */
export async function confirmDiscard() {
  if (entries.size === 0) return true;
  const label = [...entries.values()][0];
  const ok = await confirmDialog({
    title: 'Discard unsaved changes?',
    message: `${label} has unsaved changes. Leave without saving?`,
    confirmLabel: 'Discard changes',
  });
  if (ok) entries.clear();
  return ok;
}
